import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import MuiAlert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';

// MUI Alert 컴포넌트를 Snackbar에 넣기 위한 Custom Alert (forwardRef 필요)
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});

// 커스텀 툴바: 좌측은 "행 추가", 우측은 검색창 (자재명 필드 검색)
function EditToolbar(props) {
  const { setRows, setRowModesModel, searchText, onSearchChange } = props;

  const handleClick = () => {
    // 임의 id 생성 대신, 임시 값 사용 (POST 시 id는 백엔드에서 자동 생성됨)
    const tempId = `temp-${Math.random().toString(36).substr(2, 9)}`;
    setRows((oldRows) => [
      { id: tempId, type: '', name: '', specifications: '', unit: '', unitPrice: 0, isNew: true },
      ...oldRows,
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [tempId]: { mode: GridRowModes.Edit, fieldToFocus: 'type' },
    }));
  };

  return (
    <GridToolbarContainer>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          자재 추가
        </Button>
        <TextField
          variant="standard"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="자재명 검색"
          InputProps={{ style: { fontSize: '0.875rem' } }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

export default function MaterialPage({
  enableCheckbox = false,
  disablePagination = false,
  onSelectionChange,
  initialSelection = [],      // 부모(모달)로부터 초기 선택된 자재 목록을 전달
  hideDeleteAction = false,
}) {
  // 초기 더미 데이터 없이 빈 배열로 시작
  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  // 검색어 상태
  const [searchText, setSearchText] = React.useState('');
  // Alert 상태: open, message, severity('success' 또는 'error')
  const [alert, setAlert] = React.useState({ open: false, message: '', severity: 'success' });
  // 로딩 상태 추가
  const [loading, setLoading] = React.useState(true);
  // 1) "제어된" selectionModel 상태
  const [selectionModel, setSelectionModel] = useState(initialSelection.map(m => m.id));

  // 부모가 초기 선택을 바꿨을 때만 selectionModel 을 갱신
  useEffect(() => {
    setSelectionModel(initialSelection.map(m => m.id));
  }, []);

  // Snackbar Alert 닫기 핸들러
  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setAlert(prev => ({ ...prev, open: false }));
  };

  // 컴포넌트 마운트 시 /api/materials URL로부터 데이터 GET
  React.useEffect(() => {
    setLoading(true);
    fetch('/api/materials')
      .then((response) => {
        if (!response.ok) {
          throw new Error('자재 데이터를 가져오는 중 오류 발생');
        }
        return response.json();
      })
      .then((data) => {
        setRows(data);
      })
      .catch((error) => console.error('Fetch error:', error))
      .finally(() => {
        setLoading(false);
      });
  }, []);

 
  // 검색어에 따라 표시할 행 필터링 (자재명)
  const filteredRows = React.useMemo(() => {
    return rows.filter(row =>
      // 임시로 추가된 새 행은 무조건 포함(검새 후 자재추가 오류 방지)
      row.isNew === true ||
      // 아니면 기존 검색 로직
      (row.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      row.type?.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [rows, searchText]);

  // 체크박스 선택이 바뀔 때마다 호출
  const handleRowSelection = (newModel) => {
    // 1) 현재 보이지 않는(검색으로 걸러진) 행 중, 이전에 선택된 ID
    const hiddenSelected = selectionModel.filter(
      id => !filteredRows.some(row => row.id === id)
    );

    // 2) 새로 선택된 visible 항목(newModel) + hiddenSelected 합치기
    const merged = [...newModel, ...hiddenSelected];

    // 제어 모드이므로 상태 직접 갱신
    setSelectionModel(merged);

     // 4) 부모 콜백에 전달할 때도 merged 기준으로 전달
    if (onSelectionChange) {
      const selectedRows = rows.filter(row => merged.includes(row.id));
      onSelectionChange(selectedRows);
    }
  };


  // 클릭 이벤트 함수들
  // 수정 모드에서 종료 이벤트 처리
  const handleRowEditStop = async (params, event) => {
    const { reason, id } = params;
    
    // 포커스 아웃시 편집 유지
    if (reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;       // 편집 유지
      return;
    }

    // 엔터키 입력시 저장
    if (reason === GridRowEditStopReasons.enterKeyDown) {
      // DataGrid가 processRowUpdate를 호출하도록 View로 전환
      setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
      return;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => async () => {
    // 사용자에게 삭제 확인을 요청
    const isConfirmed = window.confirm("정말로 삭제하시겠습니까?");
    if (!isConfirmed) {
      return; // 확인을 클릭하지 않으면 삭제를 하지 않음
    }

    try{
      const response = await fetch(`/api/materials/${id}`,{
        method: 'DELETE',
      });
      if(!response.ok){
        throw new Error('삭제 실패');
      }
      // DELETE 요청이 성공하면 상태에서 해당 행을 제거
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      setAlert({ open: true, message: '삭제 성공', severity: 'success' });
    } catch (error){
      console.log(`삭제 에러`, error);
      setAlert({ open: true, message: '삭제 실패', severity: 'error' });
    }
  };



   // processRowUpdate: 행 저장 시 POST (생성) 또는 PUT (수정) 요청을 보내고, 성공/실패 여부에 따라 Alert 출력
  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  // processRowUpdate를 비동기 함수로 정의하여 생성/수정 API 호출
  const processRowUpdate = async (newRow) => {
    // 필수 필드 값 확인
    const { name, unit, unitPrice} = newRow;
    
    const invalidField = !name?.trim() ? 'name' : !unit?.trim() ? 'unit' : (isNaN(unitPrice) || Number(unitPrice) <= 0) ? 'unitPrice' : null;
    if (invalidField) {
      setAlert({ open: true, message: '모든 필드를 입력해 주세요.', severity: 'error' });
      const err = new Error('VALIDATION_FAILED');
      err.rowId = newRow.id;
      err.invalidField = invalidField;
      throw err; // 실패는 반드시 throw
    }
    
    // id와 isNew 필드를 제외한 나머지 필드만 payload로 생성
    const {id, isNew, ...payload} = newRow;
    let updatedRow = { ...newRow, isNew: false };

    try {
      if (newRow.isNew) {
        // 새 자재 생성: id와 isNew 제외한 payload 전송 (POST)
        const response = await fetch('/api/materials', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          setAlert({ open: true, message: '자재 생성 중 오류 발생', severity: 'error' });
          const err = new Error('CREATE_FAILED');
          err.rowId = newRow.id;
          throw err;
        }
        updatedRow = await response.json();
        setAlert({ open: true, message: '생성 성공', severity: 'success' });
      } else {
        // 기존 자재 수정 (PUT)
        const response = await fetch(`/api/materials/${newRow.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newRow),
        });
        if (!response.ok) {
          setAlert({ open: true, message: '자재 수정 중 오류 발생', severity: 'error' });
          const err = new Error('UPDATE_FAILED');
          err.rowId = newRow.id;
          throw err;
        }
        updatedRow = await response.json();
        setAlert({ open: true, message: '수정 성공', severity: 'success' });
      }
    } catch (error) {
      console.error('processRowUpdate 에러: ', error);
      setAlert({ open: true, message: error.message, severity: 'error' });
      // 필요에 따라 사용자에게 알림 처리 등을 추가
    }

    // 기존 코드에서는 updatedRow.id를 기준으로 업데이트했는데,
    // 임시 ID와 실제 DB의 ID가 다를 수 있으므로 newRow.id를 기준으로 업데이트
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? updatedRow : row))
    );

    return updatedRow;
  };



  // 자재관리 컬럼 정의: 자재명, 단위, 단가, actions 열
  const columns = [
    {
      field: 'type',
      headerName: '종류',
      headerAlign: 'center',
      align: 'center',
      editable: true
    },
    {
      field: 'name',
      headerName: '자재명',
      editable: true,
      flex: 1, // 남은 너비 모두 차지
    },
    {
      field: 'specifications',
      headerName: '규격',
      headerAlign: 'center',
      align: 'center',
      flex: 1, // 남은 너비 모두 차지
      editable: true,
    },
    {
      field: 'unit',
      headerName: '단위',
      textAlign: 'center',
      editable: true,
      headerAlign: 'center',  // 헤더 가운데 정렬
      align: 'center'         // 셀 가운데 정렬
    },
    {
      field: 'unitPrice',
      headerName: '단가',
      type: 'number',
      editable: true,
      
    },
    {
      field: 'company',
      headerName: '제조사',
      editable: true,
      headerAlign: 'center',  // 헤더 가운데 정렬
      align: 'center'         // 셀 가운데 정렬
      },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon/>}
              label="Save"
              onClick={handleSaveClick(id)}
              color="primary"
              key="save"
            />,
            <GridActionsCellItem
              icon={<CancelIcon/>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              color="inherit"
              key="cancel"
            />,
          ];
        }

        const actions = [
          <GridActionsCellItem
            icon={<EditIcon/>}
            label="Edit"
            onClick={handleEditClick(id)}
            color="inherit"
            key="edit"
          />,
        ];
        if(!hideDeleteAction){
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon/>}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
              key="delete"
            />
          );
        }
        return actions;
      },
    },
  ];

  const gridHeight = disablePagination ? '60vh' : '85vh';

  return (
    <div style={{backgroundColor: 'white', width: '100%'}}>
      {loading && <LinearProgress />}
      <Box
        sx={{
          // 고정 높이 : 페이지 스크롤 대신 그리드 내부 스크롤만 생기게
          height : gridHeight,
          width: '100%',
          '& .actions': {color: 'text.secondary',},
          '& .textPrimary': {color: 'text.primary',},
        }}
      >
        <DataGrid
          loading={loading}
          rows={filteredRows}
          rowHeight={40}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          // 에러 발생 시 해당 행을 Edit 모드로 전환하고, 에러 메시지에 따라 포커스 필드 설정
          onProgressRowUpdateError={(error) => {
            const rowId = error?.rowId;
            setRowModesModel(prev => ({
              ...prev,
              ...(rowId ? {[rowId]: { mode: GridRowModes.Edit, fieldToFocus: error.invalidField || 'name' } } : {}),
            }));
          }}
          slots={{ toolbar: EditToolbar }}
          slotProps={{
            toolbar: { setRows, setRowModesModel, searchText, onSearchChange: setSearchText },
          }}
          // DataGrid 자체 높이 주지 않고, 바깥 BOX 높이에 맞춰 내부 스크롤만
          style={{  width: '100%' }}
          // 페이지 크기 옵션을 10, 15, 20, 30, 50, 100으로 설정
          // props로 받은 값에 따라 견적서모달인지 자재관리인지 구분하고 데이터그리드 변경
          checkboxSelection={enableCheckbox}
          // 제어 모드 props
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelection}
          
          hideFooter={disablePagination}
          disableColumnMenu={disablePagination}
          hideDeleteAction={hideDeleteAction}
          initialState={
            disablePagination
              ? {}
              : { pagination: { paginationModel: { pageSize: 30 } } }
          }
          pageSizeOptions={disablePagination ? [] : [10,15,20,30,50,100]}
        />
      </Box>
      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert icon={<CheckIcon fontSize="inherit" />} onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  );
}